import React from 'react';

function PlayerEquipmentPanel({
    playerEquipment = {},
    onGearClick,
    onAddGearClick,
    showAddButton = true,
    isOpponentView = false,
    weekStatus = null,
    equippedGear = new Set(),
    useAbsolutePositioning = false,
    isMobile = false,
    isEquipmentOnlyView = false // New prop to handle equipment-only styling
}) {
    // Disable gear modification when stats are active
    const isStatsActive = weekStatus && weekStatus.stats_active;
    const canModifyGear = !isOpponentView && !isStatsActive;
    // Set consistent width for both panels
    // Make current equipment a bit wider than available gear
    const panelWidth = isMobile ? '40vw' : '250px';
    // Base container style
    const baseContainerStyle = {
        backgroundColor: '#f8f9fa',
        border: '2px solid #ddd',
        borderRadius: '8px',
        padding: isMobile ? '15px' : '20px',
        display: 'flex',
        flexDirection: 'column',
        width: panelWidth,
        maxWidth: panelWidth,
        boxSizing: 'border-box'
    };
    // Equipment-only view gets taller and positioned higher
    if (isEquipmentOnlyView) {
        baseContainerStyle.minHeight = isMobile ? '300px' : '400px';
        baseContainerStyle.maxHeight = isMobile ? '400px' : '450px';
        baseContainerStyle.marginLeft = isMobile ? '0px' : '15px';
        baseContainerStyle.marginTop = isMobile ? '-75px' : '-100px';
    } else {
        baseContainerStyle.minHeight = isMobile ? '150px' : '200px';
        baseContainerStyle.maxHeight = isMobile ? '265px' : '375px';
    }
    // Absolute positioning style (for legacy absolute positioning)
    const absolutePositioningStyle = {
        position: 'absolute',
        left: isMobile ? '200px' : '345px',
        top: '2px',
        minHeight: isMobile ? '80px' : '100px',
        maxHeight: isMobile ? '300px' : '390px',
        width: isMobile ? '120px' : '142px'
    };
    // Combine styles based on positioning mode
    const containerStyle = useAbsolutePositioning
        ? { ...baseContainerStyle, ...absolutePositioningStyle }
        : baseContainerStyle;
    // Count equipped gear
    let equippedCount = 0;
    if (playerEquipment) {
        equippedCount += ['HEAD', 'CHEST', 'HANDS', 'LEGS', 'FEET'].filter(slot => playerEquipment[slot]).length;
        if (playerEquipment.ANY && Array.isArray(playerEquipment.ANY)) {
            equippedCount += playerEquipment.ANY.length;
        }
    }
    return (
        <div style={containerStyle}>
            <h4 style={{
                margin: '0 0 15px 0',
                color: '#2c3e50',
                borderBottom: '2px solid #ecf0f1',
                paddingBottom: '8px',
                flexShrink: 0,
                fontSize: isMobile ? '14px' : '16px'
            }}>
                Player Equipment
            </h4>
            {/* Scrollable gear container */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                marginBottom: showAddButton && !isOpponentView ? '15px' : '0',
                paddingRight: '5px',
                width: '100%',
                maxWidth: '100%',
            }}>
            {playerEquipment && Object.keys(playerEquipment).length > 0 ? (
                <div>
                    {/* Show specific slot gear */}
                    {['HEAD', 'CHEST', 'HANDS', 'LEGS', 'FEET'].map(slot => {
                        const gear = playerEquipment[slot];
                        return gear ? (
                            <div
                                key={`${slot}-${gear.id}`}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px',
                                    margin: '8px 0',
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #27ae60',
                                    borderRadius: '6px',
                                    cursor: canModifyGear ? 'pointer' : 'default',
                                    boxShadow: '0 2px 6px rgba(39,174,96,0.2)',
                                    opacity: canModifyGear ? 1 : 0.7
                                }}
                                onClick={() => canModifyGear && onGearClick && onGearClick(gear.id)}
                            >
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        color: '#27ae60'
                                    }}>
                                        {gear.name} ({slot})
                                    </div>
                                    {gear.description && (
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#636e72',
                                            marginTop: '2px',
                                            fontStyle: 'italic',
                                            wordBreak: 'break-word'
                                        }}>
                                            {gear.description}
                                        </div>
                                    )}
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#7f8c8d',
                                        marginTop: '2px'
                                    }}>
                                        {isOpponentView ? 'Opponent Equipment' : isStatsActive ? 'Stats are active - cannot modify' : 'Click to unequip'}
                                    </div>
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: '#27ae60',
                                    fontWeight: 'bold',
                                    marginLeft: '10px'
                                }}>
                                    EQUIPPED
                                </div>
                            </div>
                        ) : null;
                    })}
                    {/* Show ANY slot gear */}
                    {playerEquipment.ANY && playerEquipment.ANY.length > 0 && playerEquipment.ANY.map((gear, index) => (
                        <div
                            key={`any-${gear.id}-${index}`}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px',
                                margin: '8px 0',
                                backgroundColor: '#ffffff',
                                border: '2px solid #27ae60',
                                borderRadius: '6px',
                                cursor: canModifyGear ? 'pointer' : 'default',
                                boxShadow: '0 2px 6px rgba(39,174,96,0.2)',
                                opacity: canModifyGear ? 1 : 0.7
                            }}
                            onClick={() => canModifyGear && onGearClick && onGearClick(gear.id)}
                        >
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    color: '#27ae60'
                                }}>
                                    {gear.name} (ANY)
                                </div>
                                {gear.description && (
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#636e72',
                                        marginTop: '2px',
                                        fontStyle: 'italic',
                                        wordBreak: 'break-word'
                                    }}>
                                        {gear.description}
                                    </div>
                                )}
                                <div style={{
                                    fontSize: '12px',
                                    color: '#7f8c8d',
                                    marginTop: '2px'
                                }}>
                                    {isOpponentView ? 'Opponent Equipment' : isStatsActive ? 'Stats are active - cannot modify' : 'Click to unequip'}
                                </div>
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#27ae60',
                                fontWeight: 'bold',
                                marginLeft: '10px'
                            }}>
                                EQUIPPED
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '30px 0',
                    gap: '15px'
                }}>
                    <div style={{
                        textAlign: 'center',
                        color: '#7f8c8d',
                        fontStyle: 'italic',
                        fontSize: isMobile ? '14px' : '16px'
                    }}>
                        No gear equipped
                    </div>
                    {!isOpponentView && !isStatsActive && onAddGearClick && (
                        <button
                            onClick={onAddGearClick}
                            style={{
                                padding: isMobile ? '10px 20px' : '12px 24px',
                                backgroundColor: '#1555bf',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: isMobile ? '12px' : '14px',
                                fontWeight: 'bold',
                                transition: 'opacity 0.2s',
                                opacity: 1
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.target.style.opacity = '1'}
                        >
                            EQUIP
                        </button>
                    )}
                </div>
            )}
            </div>
            {showAddButton && !isOpponentView && !isStatsActive && (
                <button
                    onClick={canModifyGear ? onAddGearClick : undefined}
                    disabled={!canModifyGear || equippedCount >= 3}
                    style={{
                        width: '100%',
                        padding: isMobile ? '10px' : '12px',
                        marginTop: '15px',
                        backgroundColor: canModifyGear && equippedCount < 3 ? '#3498db' : '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: canModifyGear && equippedCount < 3 ? 'pointer' : 'not-allowed',
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: 'bold',
                        opacity: canModifyGear && equippedCount < 3 ? 1 : 0.6
                    }}
                >
                    {equippedCount >= 3 ? 'MAX 3 EQUIPPED' : isStatsActive ? 'STATS ACTIVE - GEAR LOCKED' : 'ADJUST GEAR'}
                </button>
            )}
        </div>
    );
}

export default PlayerEquipmentPanel;
